import { BaseService } from './api'
import { 
  ProjectGraphData,
  ProjectsQueryParams as ProjectsQueryParamsType,
  ApiResponse,
  ProjectsResponse
} from '../types'
import { Project, ProjectSearchResult } from '../types/project'

// Additional types from TMA project
export interface ProjectsQueryParams {
  ids?: string[]
  offset?: number
  include_rounds?: boolean
  sort_by?: 'DATE' | 'PROJECT_NAME'
  sort_direction?: 'ASC' | 'DESC'
  search?: string
  include_logo?: boolean
}



export interface ProjectGraphResponse {
  data: ProjectGraphData
  project_name: string
  symbol: string
}

class ProjectService extends BaseService {
  async getProjects(params: ProjectsQueryParamsType = {}): Promise<Project[]> {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.ids?.length) {
        queryParams.append('ids', params.ids.join(','))
      }
      
      if (params.offset !== undefined) {
        queryParams.append('offset', params.offset.toString())
      }
      
      if (params.include_rounds !== undefined) {
        queryParams.append('include_rounds', params.include_rounds.toString())
      }
      
      if (params.sort_by) {
        queryParams.append('sort_by', params.sort_by)
      }
      
      if (params.sort_direction) {
        queryParams.append('sort_direction', params.sort_direction)
      }
      
      const response = await this.api.get<ProjectsResponse>(`/projects?${queryParams.toString()}`)
      return response.data?.data || []
    } catch (error) {
      console.error('Failed to fetch projects:', error)
      throw error
    }
  }

  async getProjectById(projectId: string): Promise<Project | null> {
    try {
      const response = await this.api.get<ApiResponse<Project>>(`/projects/${projectId}`)
      return response.data?.data || null
    } catch (error) {
      console.error(`Failed to fetch project ${projectId}:`, error)
      throw error
    }
  }

  async getProjectGraphData(projectId: string): Promise<ProjectGraphData | null> {
    try {
      const response = await this.api.get<ApiResponse<ProjectGraphData>>(`/projects/${projectId}/graph`)
      return response.data?.data || null
    } catch (error) {
      console.error(`Failed to fetch graph data for project ${projectId}:`, error)
      throw error
    }
  }

  // Search projects for portfolio assets
  async searchProjects(query: string): Promise<ProjectSearchResult[]> {
    try {
      const queryParams = { 
        search: query,
        include_logo: true
      }
      const response = await this.api.get<ApiResponse<ProjectSearchResult[]>>('/projects/ids', { params: queryParams })
      return response.data?.data || []
    } catch (error) {
      this.handleError(error)
      return []
    }
  }

  // Get project funding rounds
  async getProjectFundingRounds(projectId: string): Promise<any[]> {
    try {
      const project = await this.getProjectById(projectId)
      return project?.rounds || []
    } catch (error) {
      this.handleError(error)
    }
  }

  // Get trending projects
  async getTrendingProjects(): Promise<Project[]> {
    try {
      return await this.getProjects({ 
        sort_by: 'DATE',
        sort_direction: 'DESC',
        include_rounds: true
      })
    } catch (error) {
      this.handleError(error)
    }
  }
}

export const projectService = new ProjectService() 